/**
 * constants.js — Shared UI constants for Project 217
 *
 * Fulfillment status options and colors are defined here so the
 * Fulfillment Tracker, Fulfillment Dashboard, and any other feature
 * that references fulfillment statuses stay in sync.
 *
 * Usage:
 *   import { STATUS_OPTIONS, STATUS_STYLES, STATUS_COLORS } from "../utils/constants";
 */

/** Ordered list of all valid fulfillment statuses */
export const STATUS_OPTIONS = [
  "Pending",
  "Sent",
  "Boxed",
  "Broken Item",
  "Re-boxed",
  "Resent",
  "Hold",
  "Cancelled",
  "Data Check",
];

/**
 * Statuses that only become selectable once a record has already
 * entered the broken-item flow (i.e. current status is Broken Item,
 * Re-boxed, or Resent). Used by FulfillmentTracker's per-row dropdown
 * to hide Re-boxed/Resent until they're actually reachable.
 */
export const BROKEN_ITEM_FLOW = ["Broken Item", "Re-boxed", "Resent"];

/**
 * CSS-var-based style objects for status dropdowns and inline styling.
 * Keys match STATUS_OPTIONS exactly.
 *
 * Re-boxed and Resent intentionally reuse the Boxed / Sent tokens —
 * same physical state, just the second time around — so retuning
 * Boxed/Sent colors later updates these automatically.
 */
export const STATUS_STYLES = {
  "Pending":     { background: "var(--status-pending-bg)",     color: "var(--status-pending-text)",     borderColor: "var(--status-pending-border)" },
  "Sent":        { background: "var(--status-sent-bg)",        color: "var(--status-sent-text)",        borderColor: "var(--status-sent-border)" },
  "Boxed":       { background: "var(--status-boxed-bg)",       color: "var(--status-boxed-text)",       borderColor: "var(--status-boxed-border)" },
  "Broken Item": { background: "var(--status-brokenitem-bg)",  color: "var(--status-brokenitem-text)",  borderColor: "var(--status-brokenitem-border)" },
  "Re-boxed":    { background: "var(--status-boxed-bg)",       color: "var(--status-boxed-text)",       borderColor: "var(--status-boxed-border)" },
  "Resent":      { background: "var(--status-sent-bg)",        color: "var(--status-sent-text)",        borderColor: "var(--status-sent-border)" },
  "Hold":        { background: "var(--status-hold-bg)",        color: "var(--status-hold-text)",        borderColor: "var(--status-hold-border)" },
  "Cancelled":   { background: "var(--status-cancelled-bg)",   color: "var(--status-cancelled-text)",   borderColor: "var(--status-cancelled-border)" },
  "Data Check":  { background: "var(--status-datacheck-bg)",   color: "var(--status-datacheck-text)",   borderColor: "var(--status-datacheck-border)" },
};

/**
 * Tailwind color classes keyed by status string.
 * Used where inline styles aren't appropriate (e.g. Badge variants).
 */
export const STATUS_COLORS = {
  "Pending":     "bg-yellow-100 text-yellow-800",
  "Sent":        "bg-green-100 text-green-800",
  "Boxed":       "bg-cyan-100 text-cyan-800",
  "Broken Item": "bg-rose-100 text-rose-800",
  "Re-boxed":    "bg-cyan-100 text-cyan-800",
  "Resent":      "bg-green-100 text-green-800",
  "Hold":        "bg-blue-100 text-blue-800",
  "Cancelled":   "bg-red-100 text-red-800",
  "Data Check":  "bg-purple-100 text-purple-800",
};

/**
 * Badge variant names keyed by status string.
 * Used in SummaryBar and anywhere a Badge component renders a status.
 */
export const STATUS_VARIANTS = {
  "Pending":     "warning",
  "Sent":        "success",
  "Boxed":       "info",
  "Broken Item": "error",
  "Re-boxed":    "info",
  "Resent":      "success",
  "Hold":        "info",
  "Cancelled":   "error",
  "Data Check":  "orange",
};

/**
 * Life Member Box fulfillment status — tracked separately from the
 * main Status field, shown only on records whose TransactionTag
 * contains "Life" (Life, Gifted Life, Gifted Life Benefactor,
 * Gifted Life Recipient). Reuses existing design tokens rather than
 * minting new ones, since these four states map cleanly onto
 * colors already used elsewhere (muted/warning/info/success).
 */
export const LM_BOX_STATUS_OPTIONS = ["Pending", "Hold", "Boxed", "Sent"];

export const LM_BOX_STATUS_STYLES = {
  "Pending": { background: "var(--color-surface-alt)", color: "var(--color-text-muted)",  borderColor: "var(--color-border)" },
  "Hold":    { background: "var(--color-warning-bg)",  color: "var(--color-warning)",     borderColor: "var(--color-warning-border)" },
  "Boxed":   { background: "var(--color-info-bg)",     color: "var(--color-info)",        borderColor: "var(--color-info-border)" },
  "Sent":    { background: "var(--color-success-bg)",  color: "var(--color-success)",     borderColor: "var(--color-success-border)" },
};

/**
 * Bulk-action values for Life Member Box status. Only Sent and Pending
 * are offered in bulk (see TableHeader) — Hold and Boxed are treated
 * as per-record judgment calls, edited individually via DetailsPanel.
 */
export const LM_BOX_BULK_OPTIONS = ["Sent", "Pending"];